import React, { useMemo, useState } from 'react';
import { Plus, Minus, Calendar, CalendarRange, ArrowRight } from 'lucide-react';
import { addDays, addWorkingDays } from '../../../../../utils/calculations/date';

/**
 * Компонент для добавления и вычитания времени от даты
 */
const DateAddSubtract = ({ 
  formData, 
  setFormData, 
  results, 
  isCalculating,
  holidays
}) => {
  const [showExample, setShowExample] = useState(false);
  
  // Список примеров для разных типов периодов
  const examples = useMemo(() => {
    if (!formData.startDate) return [];
    
    const startDate = new Date(formData.startDate);
    const today = new Date();
    
    return [
      { 
        title: 'Через 30 дней', 
        operation: 'add',
        datePeriod: 30,
        periodType: 'days',
        result: addDays(startDate, 30)
      },
      { 
        title: 'Через 2 недели', 
        operation: 'add',
        datePeriod: 2,
        periodType: 'weeks',
        result: addDays(startDate, 14)
      },
      { 
        title: 'Через 3 месяца', 
        operation: 'add',
        datePeriod: 3,
        periodType: 'months',
        result: new Date(new Date(startDate).setMonth(startDate.getMonth() + 3))
      },
      { 
        title: 'Год назад', 
        operation: 'subtract',
        datePeriod: 1,
        periodType: 'years',
        result: new Date(new Date(startDate).setFullYear(startDate.getFullYear() - 1))
      },
      { 
        title: '30 рабочих дней', 
        operation: 'add',
        datePeriod: 30,
        periodType: 'days',
        onlyWorkingDays: true,
        result: addWorkingDays(startDate, 30, holidays.map(h => new Date(h.date)))
      }
    ];
  }, [formData.startDate, holidays]);
  
  // Примеры пользовательских сценариев расчета
  const scenarios = [
    {
      title: 'Окончание отпуска',
      description: 'Если отпуск начинается 15 июля и длится 14 дней, когда нужно выходить на работу?',
      operation: 'add',
      datePeriod: 14,
      periodType: 'days',
      date: '15 июля',
      result: '29 июля'
    },
    {
      title: 'Срок выполнения проекта',
      description: 'Проект должен быть выполнен за 30 рабочих дней с 1 сентября. Когда будет дедлайн?',
      operation: 'add',
      datePeriod: 30,
      periodType: 'days',
      onlyWorkingDays: true,
      date: '1 сентября',
      result: '13 октября'
    },
    {
      title: 'Дата зачатия',
      description: 'Если предполагаемая дата родов 10 мая, то когда примерно произошло зачатие?',
      operation: 'subtract',
      datePeriod: 280,
      periodType: 'days',
      date: '10 мая',
      result: '3 августа предыдущего года'
    },
    {
      title: 'Окончание испытательного срока',
      description: 'Работник принят 5 марта с испытательным сроком 3 месяца. Когда он закончится?',
      operation: 'add',
      datePeriod: 3,
      periodType: 'months',
      date: '5 марта',
      result: '5 июня'
    },
    {
      title: 'Возврат товара',
      description: 'Товар куплен 20 сентября. Срок возврата 14 дней. До какой даты можно вернуть?',
      operation: 'add',
      datePeriod: 14,
      periodType: 'days',
      date: '20 сентября',
      result: '4 октября'
    }
  ];
  
  // Применение примера из списка к форме
  const applyExample = (example) => {
    setFormData(prev => ({
      ...prev,
      operation: example.operation,
      datePeriod: example.datePeriod,
      periodType: example.periodType,
      onlyWorkingDays: example.onlyWorkingDays || false
    }));
  };
  
  // Форматирование даты для отображения
  const formatDate = (date) => {
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      weekday: 'long'
    });
  };
  
  if (isCalculating || !formData.startDate) {
    return null;
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8 mb-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        {formData.operation === 'add' ? (
          <Plus className="w-5 h-5 mr-2 text-green-500" />
        ) : (
          <Minus className="w-5 h-5 mr-2 text-red-500" />
        )}
        {formData.operation === 'add' ? 'Добавление к дате' : 'Вычитание из даты'}
      </h3>
      
      {/* Визуальная схема операции */}
      <div className="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg mb-6">
        <div className="text-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
          <Calendar className="w-5 h-5 mx-auto mb-1 text-cyan-500" />
          <div className="text-sm font-medium">{formatDate(new Date(formData.startDate))}</div>
        </div>
        
        <div className="mx-4 flex items-center">
          {formData.operation === 'add' ? (
            <Plus className="w-6 h-6 text-green-500" />
          ) : (
            <Minus className="w-6 h-6 text-red-500" />
          )}
          <div className="ml-2 text-sm font-medium">
            {formData.datePeriod} {' '}
            {formData.periodType === 'days' && `${formData.datePeriod === 1 ? 'день' : formData.datePeriod < 5 ? 'дня' : 'дней'}`}
            {formData.periodType === 'weeks' && `${formData.datePeriod === 1 ? 'неделя' : formData.datePeriod < 5 ? 'недели' : 'недель'}`}
            {formData.periodType === 'months' && `${formData.datePeriod === 1 ? 'месяц' : formData.datePeriod < 5 ? 'месяца' : 'месяцев'}`}
            {formData.periodType === 'years' && `${formData.datePeriod === 1 ? 'год' : formData.datePeriod < 5 ? 'года' : 'лет'}`}
            {formData.onlyWorkingDays && formData.periodType === 'days' && ' (только рабочие)'}
          </div>
        </div>
        
        <ArrowRight className="w-5 h-5 text-gray-400 dark:text-gray-500 mx-2" />
        
        {results ? (
          <div className="text-center px-4 py-2 bg-white dark:bg-gray-800 border border-cyan-200 dark:border-cyan-700 rounded-lg shadow-sm">
            <Calendar className="w-5 h-5 mx-auto mb-1 text-cyan-500" />
            <div className="text-sm font-medium">{formatDate(new Date(results.resultDate))}</div>
          </div>
        ) : (
          <div className="text-center px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
            <div className="text-sm text-gray-500 dark:text-gray-400">Результат</div>
          </div>
        )}
      </div>
      
      {/* Примеры для быстрого вычисления */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => setShowExample(!showExample)}
          className="flex items-center text-sm font-medium text-cyan-600 dark:text-cyan-400 mb-3 hover:text-cyan-800 dark:hover:text-cyan-300 transition-colors"
        >
          <CalendarRange className="w-4 h-4 mr-1" />
          {showExample ? 'Скрыть примеры' : 'Показать примеры расчета'}
        </button>
        
        {showExample && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {examples.map((example, index) => (
              <button
                key={index}
                type="button"
                onClick={() => applyExample(example)}
                className="p-3 text-sm text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="font-medium mb-1 flex items-center">
                  {example.operation === 'add' ? (
                    <Plus className="w-4 h-4 mr-1 text-green-500" />
                  ) : (
                    <Minus className="w-4 h-4 mr-1 text-red-500" />
                  )}
                  {example.title}
                </div>
                <div className="text-gray-500 dark:text-gray-400 text-xs">
                  Результат: {formatDate(example.result)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Примеры сценариев использования */}
      <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-4 mb-4 border border-cyan-100 dark:border-cyan-800">
        <h4 className="text-sm font-medium text-cyan-800 dark:text-cyan-200 mb-2">
          Распространенные сценарии использования
        </h4>
        
        <div className="space-y-2">
          {scenarios.map((scenario, index) => (
            <div key={index} className="text-sm">
              <div className="font-medium text-cyan-700 dark:text-cyan-300">
                {scenario.title}
              </div>
              <div className="text-cyan-600 dark:text-cyan-400">
                {scenario.description}
              </div>
              <div className="mt-1 text-xs text-cyan-700 dark:text-cyan-300">
                {scenario.date} {scenario.operation === 'add' ? '+' : '-'} {scenario.datePeriod} {scenario.periodType} 
                {scenario.onlyWorkingDays ? ' (рабочих)' : ''} = {scenario.result}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Дополнительная информация */}
      {results && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-4">
          <div className="flex items-center mb-2">
            <Calendar className="w-4 h-4 mr-2 text-cyan-500" />
            <span className="font-medium">Информация о результате</span>
          </div>
          
          <div>
            {results.resultDate > new Date() ? (
              <p>Результат находится в будущем, через {Math.ceil((new Date(results.resultDate) - new Date()) / (1000 * 60 * 60 * 24))} дней от сегодняшней даты.</p>
            ) : results.resultDate < new Date() ? (
              <p>Результат находится в прошлом, {Math.ceil((new Date() - new Date(results.resultDate)) / (1000 * 60 * 60 * 24))} дней назад от сегодняшней даты.</p>
            ) : (
              <p>Результат - сегодняшняя дата.</p>
            )}
            
            <p className="mt-1">
              День недели: <span className="font-medium">{new Date(results.resultDate).toLocaleDateString('ru-RU', { weekday: 'long' })}</span>
            </p>
            
            {formData.periodType === 'days' && formData.datePeriod > 7 && (
              <p className="mt-1">
                Это примерно {Math.floor(formData.datePeriod / 7)} {Math.floor(formData.datePeriod / 7) === 1 ? 'неделя' : Math.floor(formData.datePeriod / 7) < 5 ? 'недели' : 'недель'}
                {formData.datePeriod % 7 > 0 ? ` и ${formData.datePeriod % 7} ${formData.datePeriod % 7 === 1 ? 'день' : formData.datePeriod % 7 < 5 ? 'дня' : 'дней'}` : ''}
              </p>
            )}
            
            {formData.periodType === 'days' && formData.datePeriod > 30 && (
              <p className="mt-1">
                Это примерно {Math.floor(formData.datePeriod / 30)} {Math.floor(formData.datePeriod / 30) === 1 ? 'месяц' : Math.floor(formData.datePeriod / 30) < 5 ? 'месяца' : 'месяцев'}
                {formData.datePeriod % 30 > 0 ? ` и ${formData.datePeriod % 30} ${formData.datePeriod % 30 === 1 ? 'день' : formData.datePeriod % 30 < 5 ? 'дня' : 'дней'}` : ''}
              </p>
            )}
            
            {formData.onlyWorkingDays && formData.periodType === 'days' && (
              <p className="mt-1">
                При использовании только рабочих дней, выходные (суббота, воскресенье) и праздничные дни не учитываются в расчете.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateAddSubtract;